<script setup lang="ts">
import { ALIconButton, ALTooltip, type TooltipPosition } from '@af-lib/vue-ui';
import { onBeforeUnmount, ref, useId } from 'vue';

import { copyData } from '@/helpers/copy';

type Props = {
  data: string;
  size?: 'xxs' | 'xs' | 's' | 'm' | 'l';
  position?: TooltipPosition;
};
const props = withDefaults(defineProps<Props>(), {
  size: 'xs',
  position: 'top'
});
const emits = defineEmits<{
  (e: 'copy-done');
}>();

const isCopied = ref(false);

let timer;

const id = `copy-${useId()}`;

async function copy() {
  await copyData(props.data);
  isCopied.value = true;
  emits('copy-done');
}

function leaveIcon() {
  timer = setTimeout(() => (isCopied.value = false), 500);
}

// SETUP
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});
</script>
<template>
  <ALTooltip :position="position" :closeDelay="isCopied ? 50 : 0" :closeOnPointerDown="false">
    <template #trigger>
      <div @click.stop.prevent="copy" @mouseleave="leaveIcon">
        <slot>
          <ALIconButton
            variant="tertiary"
            icon="fa fa-copy"
            :size="props.size"
            :ariaLabel="$t('general.copy.action')"
            :describedBy="id"
          />
        </slot>
      </div>
    </template>
    <template #content>
      <span :id="id">{{ isCopied ? $t('general.copy.copied') : $t('general.copy.action') }}</span>
    </template>
  </ALTooltip>
</template>
